/**
 * Where the game server lives. Empty (the default) means the client and the
 * server share an origin. When the client is hosted as a static site (e.g.
 * leebin.win/dominion) while the server runs elsewhere (e.g. Render), the
 * build sets JD_API_BASE to the server's absolute URL and all API/WebSocket
 * traffic goes there instead.
 */
export const API_BASE: string = __JD_API_BASE__.replace(/\/+$/, '');

/**
 * The game session token, kept in memory + sessionStorage. The session is
 * also an HttpOnly cookie, but iOS browsers (WebKit ITP) refuse to send
 * cross-site cookies when the client and the server live on different
 * domains — so the client presents this token explicitly instead
 * (Authorization header for REST, ?token= for the WebSocket handshake).
 */
const TOKEN_KEY = 'jd.session.token';
let memoryToken: string | null = null;

export function getSessionToken(): string | null {
  if (memoryToken) return memoryToken;
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setSessionToken(token: string | null): void {
  memoryToken = token;
  try {
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    // storage unavailable (private mode etc.) — the in-memory copy still works
  }
}

/** WebSocket subprotocol the game speaks (the server echoes it back). */
export const WS_PROTOCOL = 'jianghu.v1';

/**
 * WebSocket URL for entering the world with the given character. The session token is NOT put
 * in the URL (URLs end up in proxy and hosting access logs); see `wsProtocols`.
 */
export function wsUrl(characterId: string): string {
  const query = `?character=${encodeURIComponent(characterId)}`;
  if (API_BASE) {
    const u = new URL(API_BASE);
    return `${u.protocol === 'https:' ? 'wss:' : 'ws:'}//${u.host}/ws${query}`;
  }
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}/ws${query}`;
}

/**
 * Subprotocols offered on the WebSocket handshake. Browsers cannot set an Authorization header on
 * a WebSocket, so when the cross-site cookie is unavailable (iOS ITP) the session token travels as
 * an extra `jh-auth.<token>` subprotocol in the Sec-WebSocket-Protocol request header, which is
 * not logged like a URL. The server only ever echoes `jianghu.v1` back.
 */
export function wsProtocols(): string[] {
  const token = getSessionToken();
  return token ? [WS_PROTOCOL, `jh-auth.${token}`] : [WS_PROTOCOL];
}
